"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { trackEvent } from "@/lib/analytics/track";
import { TEMPLATES, templateById } from "@/lib/resumeRender/catalog";
import { exampleResume } from "@/lib/resumeBuilder/example";
import { downloadJson } from "@/lib/resumeBuilder/exporters";
import { newId, newResume, nowIso, parseResume } from "@/lib/resumeBuilder/model";
import { duplicateResume } from "@/lib/resumeBuilder/ops";
import { resumeSpec } from "@/lib/resumeBuilder/spec";
import { deleteResume, listResumes, saveResume } from "@/lib/resumeBuilder/storage";
import type { Resume } from "@/lib/resumeBuilder/types";
import { timeAgo } from "./dialogs";
import { IconCode, IconCopy, IconMore, IconPlus, IconSparkle, IconTrash, IconUpload, IconFile } from "./icons";
import PageThumb from "./PageThumb";
import { BTN_ICON, BTN_PRIMARY, BTN_SECONDARY, ConfirmDialog, Menu } from "./ui";

// The builder's home: your resumes (stored in this browser) with live
// thumbnails, and every way to start a new one.

/** Page 1 of a resume (optionally in another template). */
function Thumb({ resume, templateId, label }: { resume: Resume; templateId?: string; label?: string }) {
  const spec = useCallback(() => resumeSpec(resume, { placeholders: true, templateId }), [resume, templateId]);
  return <PageThumb spec={spec} label={label} />;
}

function useResumes() {
  const [list, setList] = useState<Resume[] | null>(null);
  const reload = () => listResumes().then(setList);
  useEffect(() => {
    void reload();
  }, []);
  return { list, reload };
}

export function useCreate() {
  const router = useRouter();
  return async (r: Resume, source: string, extra = "") => {
    await saveResume(r);
    trackEvent("resume_builder_create", { source, template: r.templateId ?? "aurora" });
    router.push(`/resume-builder/edit?id=${encodeURIComponent(r.id)}${extra}`);
  };
}

/** The "start" buttons: blank, example, from a sample, import. */
export function StartActions({ compact = false }: { compact?: boolean }) {
  const create = useCreate();
  const fileInput = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);
  return (
    <div>
      <div className={`flex flex-wrap items-center gap-3 ${compact ? "" : "justify-center lg:justify-start"}`}>
        <button type="button" className={`${BTN_PRIMARY} !px-6 !py-3`} onClick={() => create(newResume(), "blank")}>
          <IconPlus size={17} />
          Create my resume
        </button>
        <button type="button" className={`${BTN_SECONDARY} !px-5 !py-3`} onClick={() => create(newResume(), "sample-picker", "&sample=1")}>
          <IconSparkle size={16} />
          Start from a job sample
        </button>
        <button type="button" className={`${BTN_SECONDARY} !px-5 !py-3`} onClick={() => create(exampleResume(), "example")}>
          <IconFile size={16} />
          Open an example
        </button>
        <button type="button" className="inline-flex items-center gap-1.5 px-2 py-3 text-sm font-semibold text-slate-600 underline-offset-4 hover:underline dark:text-slate-300" onClick={() => fileInput.current?.click()}>
          <IconUpload size={16} />
          Import backup
        </button>
      </div>
      {error ? <p className="mt-2 text-sm text-rose-600">{error}</p> : null}
      <input
        ref={fileInput}
        type="file"
        accept="application/json,.json"
        className="hidden"
        onChange={async (e) => {
          const f = e.target.files?.[0];
          e.target.value = "";
          if (!f) return;
          try {
            const r = parseResume(JSON.parse(await f.text()));
            const now = nowIso();
            await create({ ...r, id: newId(), createdAt: now, updatedAt: now }, "import");
          } catch {
            setError("That file isn’t a resume backup (.json from this builder or the Nohaya Career app).");
          }
        }}
      />
    </div>
  );
}

function ResumeCard({ resume, onChanged }: { resume: Resume; onChanged: () => void }) {
  const router = useRouter();
  const [confirm, setConfirm] = useState(false);
  const template = templateById(resume.templateId);
  const href = `/resume-builder/edit?id=${encodeURIComponent(resume.id)}`;
  return (
    <div className="group relative overflow-hidden rounded-[1.25rem] bg-white shadow-[0_14px_34px_-22px_rgba(15,23,42,0.35)] ring-1 ring-slate-200/70 transition hover:-translate-y-0.5 dark:bg-white/[0.04] dark:ring-white/10">
      <Link href={href} className="block border-b border-slate-100 dark:border-white/10" aria-label={`Open ${resume.title}`}>
        <Thumb resume={resume} label={`${resume.title} preview`} />
      </Link>
      <div className="flex items-start gap-2 px-4 py-3">
        <Link href={href} className="min-w-0 flex-1">
          <p className="truncate text-sm font-bold text-slate-900 dark:text-white">{resume.title}</p>
          <p className="truncate text-[11px] text-slate-500 dark:text-slate-400">
            {template.info.name} · edited {timeAgo(resume.updatedAt)}
          </p>
        </Link>
        <Menu
          label={`${resume.title} options`}
          trigger={() => (
            <span role="button" tabIndex={0} className={`${BTN_ICON} -mr-2`}>
              <IconMore size={18} />
            </span>
          )}
          items={[
            { label: "Open", onSelect: () => router.push(href) },
            {
              label: "Duplicate",
              icon: <IconCopy size={16} />,
              onSelect: async () => {
                await saveResume(duplicateResume(resume));
                onChanged();
              },
            },
            { label: "Download backup (.json)", icon: <IconCode size={16} />, onSelect: () => downloadJson(resume) },
            "divider",
            { label: "Delete", icon: <IconTrash size={16} />, danger: true, onSelect: () => setConfirm(true) },
          ]}
        />
      </div>
      {confirm ? (
        <ConfirmDialog
          title="Delete this resume?"
          message={`“${resume.title}” and its saved versions will be removed from this browser. This can’t be undone.`}
          confirm="Delete"
          danger
          onConfirm={async () => {
            await deleteResume(resume.id);
            onChanged();
          }}
          onClose={() => setConfirm(false)}
        />
      ) : null}
    </div>
  );
}

/** "My resumes" — shown only when this browser has some. */
export function MyResumes() {
  const { list, reload } = useResumes();
  const create = useCreate();
  if (!list?.length) return null;
  return (
    <section aria-labelledby="my-resumes" className="mx-auto max-w-site px-4 sm:px-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 id="my-resumes" className="text-2xl font-bold tracking-tight text-[#0b0f19] dark:text-white">
            My resumes
          </h2>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">Saved in this browser — nothing is uploaded.</p>
        </div>
        <button type="button" className={BTN_PRIMARY} onClick={() => create(newResume(), "blank")}>
          <IconPlus size={16} /> New resume
        </button>
      </div>
      <div className="mt-6 grid grid-cols-[repeat(auto-fill,minmax(236px,1fr))] gap-5">
        {list.map((r) => (
          <ResumeCard key={r.id} resume={r} onChanged={reload} />
        ))}
      </div>
    </section>
  );
}

/** The ten templates, each drawn with the example resume; picking one starts a resume in it. */
export function TemplatePicker() {
  const create = useCreate();
  const example = useMemo(() => exampleResume(), []);
  return (
    <div className="grid grid-cols-2 gap-5 sm:grid-cols-3 lg:grid-cols-5">
      {TEMPLATES.map((t) => (
        <button
          key={t.info.id}
          type="button"
          onClick={() => create(newResume({ templateId: t.info.id }), "template", "")}
          className="group overflow-hidden rounded-[1.25rem] bg-white text-left shadow-[0_14px_34px_-22px_rgba(15,23,42,0.35)] ring-1 ring-slate-200/70 transition hover:-translate-y-1 hover:shadow-[0_22px_44px_-22px_rgba(15,23,42,0.45)] dark:bg-white/[0.04] dark:ring-white/10"
          aria-label={`Start a resume with the ${t.info.name} template`}
        >
          <div className="relative border-b border-slate-100 dark:border-white/10">
            <Thumb resume={example} templateId={t.info.id} />
            <span className="absolute inset-x-3 bottom-3 translate-y-2 rounded-full bg-[#0b0f19] py-2 text-center text-xs font-semibold text-white opacity-0 transition group-hover:translate-y-0 group-hover:opacity-100 dark:bg-white dark:text-[#0b0f19]">
              Use {t.info.name}
            </span>
          </div>
          <div className="px-3 py-2.5">
            <p className="text-sm font-bold text-slate-900 dark:text-white">{t.info.name}</p>
            <p className="truncate text-[11px] text-slate-500 dark:text-slate-400">
              {t.info.layout}
              {t.info.atsFriendly ? " · ATS-safe" : ""}
            </p>
          </div>
        </button>
      ))}
    </div>
  );
}
